'use client';

import { useNavigate } from 'react-router-dom';

import { TriviaResultDesktop, TriviaResultMobile } from './TriviaResult';
import { ClosedTrivias } from './components/ClosedTrivias';
import { InitTrivia } from './components/InitTrivia';
import { TriviaContentLeft } from './components/TriviaContentLeft';
import { TriviaQuestions } from './components/TriviaQuestions';
import { useTriviaSection } from './hooks/useTriviaSection';

import triviaMobile from '@/assets/trivia_mobile.jpg';
import { Button, Loader, NavBar } from '@/components';
import ROUTES from '@/router/routes';

interface TriviaSectionProps {
  isMobile?: boolean;
  isInDashboard?: boolean;
}

const TriviaSection = ({
  isMobile = false,
  isInDashboard = false,
}: TriviaSectionProps) => {
  const navigate = useNavigate();
  const {
    step,
    showStats,
    totalStats,
    setTotalStats,
    triviaInit,
    isLoading,
    isRefetching,
    handleInitQuestions,
    handleShowStats,
    comeBackTo,
    isWinner,
  } = useTriviaSection({ isMobile });

  // Mobile dashboard preview
  if (isMobile && isInDashboard) {
    return (
      <div className="w-full flex flex-col justify-center bg-white rounded-lg">
        <img
          alt="Imagen Trivia Gran DT"
          className="w-full rounded-t-lg "
          src={triviaMobile}
        />
        <div className="flex gap-x-1 pt-5 pb-7 px-5">
          <span className="font-semibold text-gdt-gray-2">
            Participá por medallas todas las fechas
          </span>
          <div>
            <Button
              className="px-8"
              type="button"
              variant="secondary"
              onClick={() => navigate(ROUTES.trivia)}
            >
              Jugar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const renderStepContent = () => {
    switch (step) {
      case 0:
        return (
          <InitTrivia
            handleTotalStats={handleShowStats}
            triviaData={triviaInit}
            onNext={handleInitQuestions}
          />
        );
      case 1:
        return <TriviaQuestions triviaData={triviaInit} />;
      case 2:
        return isMobile ? (
          <TriviaResultMobile
            comeBackTo={comeBackTo}
            handleShowStats={handleShowStats}
            setTotalStats={setTotalStats}
            totalStats={totalStats}
            triviaData={triviaInit}
          />
        ) : (
          <TriviaResultDesktop
            comeBackTo={comeBackTo}
            handleShowStats={handleShowStats}
            showStats={showStats}
            triviaData={triviaInit}
          />
        );
      case 3:
        return (
          <ClosedTrivias
            handleShowStats={handleShowStats}
            played={triviaInit?.resultadoParticipacion !== undefined}
          />
        );
      default:
        return null;
    }
  };

  const content = (
    <>
      <Loader isVisible={isLoading || isRefetching} />
      {isMobile ? (
        <div className="w-full rounded-lg bg-white">{renderStepContent()}</div>
      ) : (
        <div className="w-full grid grid-cols-12 rounded-lg bg-white h-[26rem]">
          <div className="col-span-5 h-full">
            <TriviaContentLeft
              isTriviaClosed={step === 3}
              isWinner={isWinner}
              triviaInit={triviaInit}
            />
          </div>
          <div className="col-span-7 h-full">{renderStepContent()}</div>
        </div>
      )}
    </>
  );

  if (isMobile) {
    return (
      <NavBar
        mobile={{
          leftContent: {
            backAction: () => navigate(ROUTES.summary),
          },
          centerContent: {
            title: 'Trivias',
          },
        }}
      >
        {content}
      </NavBar>
    );
  }

  return content;
};

// Componentes de conveniencia para mantener compatibilidad
export const TriviaSectionDesktop = () => <TriviaSection isMobile={false} />;

export const TriviaSectionMobile = ({
  isInDashboard,
}: {
  isInDashboard?: boolean;
}) => <TriviaSection isInDashboard={isInDashboard} isMobile={true} />;

export default TriviaSection;
